import { useEffect, useState } from "react";
import { Box, TableCell, TableRow, Typography } from "@mui/material";
import { Pencil, Trash2 } from "lucide-react";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { ENVIRONMENT } from "../../environment/environment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { useDebounce } from "../../shared/utils/useDebounce/use-debounce";
import { toastError } from "../../shared/services/errors/erros-handler";
import { obraService } from "./service/obra.service";
import {
  ConstructionMovementEnum,
  WorkStatusEnum,
} from "../../shared/utils/enum/enum";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import { MovementTypeEnum } from "../financeiro/schema/gerar-movimentacao-obra.schema";
import { getStatusLabel } from "./contracts/criar-obra.contract";
import { RetornoDaListagemVazia } from "../../shared/components/retornoDaListagem/retornoDaListagem";
import { Badge, IconButton, ProgressBar, Spinner } from "../../shared/components/ui";

type BadgeColor = "primary" | "secondary" | "success" | "warning" | "danger" | "neutral";

const workStatusColor: Record<WorkStatusEnum, BadgeColor> = {
  [WorkStatusEnum.ACTIVE]: "success",
  [WorkStatusEnum.FINISHED]: "success",
  [WorkStatusEnum.IN_PROGRESS]: "primary",
  [WorkStatusEnum.STAR]: "primary",
  [WorkStatusEnum.FINALIZING]: "warning",
  [WorkStatusEnum.PAUSED]: "warning",
  [WorkStatusEnum.WAITING_CONFIRMATION]: "warning",
  [WorkStatusEnum.CANCELED]: "danger",
  [WorkStatusEnum.DISABLED]: "danger",
};

interface Client {
  id: string;
  fantasyName: string;
  fullName: string;
}

interface FinancialMovement {
  id: string;
  value: number;
  movementType: MovementTypeEnum;
  status: ConstructionMovementEnum;
}

interface Movements {
  id: string;
  additionalValue: number;
  status: ConstructionMovementEnum;
}

interface Work {
  id: string;
  workName: string;
  description: string;
  city: string;
  state: string;
  neighborhood: string;
  street: string;
  number: string;
  complement: string;
  zipCode: string;
  slug: string;
  totalValue: number;
  startDate: string;
  endDate: string;
  status: WorkStatusEnum;
  stageOfWork: WorkStatusEnum;
  contractFile: string;
  createdAt: string;
  updatedAt: string;
  idClient: string;
  idCompany: string;
  client: Client;
  financiMovements: FinancialMovement[];
  movements: Movements[];
}

export function ListagemObras() {
  const [obras, setObras] = useState<Work[]>([]);
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const buscaDebounce = useDebounce(busca, 500);
  const navigate = useNavigate();

  const carregarobras = async (
    filtro?: string,
    pagina = page,
    limite = rowsPerPage
  ) => {
    try {
      setIsLoading(true);
      const result = await obraService.findAll(filtro, pagina, limite);
      setObras(result.items || []);
      setTotalCount(result.totalItems || 0);
    } catch (error) {
      setObras([]);
      toastError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    carregarobras(undefined, 0);
  }, []);

  useEffect(() => {
    setPage(0);
    carregarobras(buscaDebounce, 0);
  }, [buscaDebounce]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarobras(buscaDebounce, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    carregarobras(buscaDebounce, 0, newLimit);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir esta obra?")) {
      try {
        const obraDeletada = await obraService.deletar(id);
        toast.success(obraDeletada);
        carregarobras(buscaDebounce, page, rowsPerPage);
      } catch (error) {
        toastError(error);
      }
    }
  };

  return (
    <BaseLayout
      title="Listagem de Obras"
      barraDeFerramentas={
        <FerramanetasDaListagem
          placeholder={ENVIRONMENT.INPUT_DE_BUSCA + " por nome"}
          textoBotaoNovo="Adicionar Nova"
          mostrarInputBusca
          textoDaBusca={busca}
          aoMudarTextoDeBusca={setBusca}
          aoClicarBotaoNovo={() => navigate("/obra/criar")}
        />
      }
    >
      <Box display="flex" flexDirection="column" paddingBottom="40px">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <>
            <TabelaDaListagem
              ariaLabel="tabela de obras"
              headTable={[
                "Nome",
                "Cliente",
                "Situação",
                "Status",
                "Saldo da Obra",
                "Adicionais",
                "Ações",
              ]}
              columnWidths={[250, 200, 200, 120, 180, 150]}
              tableBody={
                obras.length > 0 ? (
                  obras.map((obra) => {
                    const entradasObra =
                      obra.financiMovements
                        ?.filter(
                          (item: FinancialMovement) =>
                            item.movementType === MovementTypeEnum.INCOME &&
                            item.status !== ConstructionMovementEnum.CANCELED
                        )
                        ?.reduce((acc, item) => acc + item.value, 0) || 0;

                    const saldoRestante = obra.totalValue - entradasObra;
                    const percentualRecebido =
                      obra.totalValue > 0
                        ? (entradasObra / obra.totalValue) * 100
                        : 0;

                    return (
                      <TableRow hover key={obra.id}>
                        <TableCell sx={{ minWidth: 250 }}>
                          {obra.workName}
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          {obra.client?.fantasyName || obra.client?.fullName || "-"}
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Badge color={workStatusColor[obra.status]}>
                            {getStatusLabel(obra.status)}
                          </Badge>
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <Badge color={workStatusColor[obra.stageOfWork]}>
                            {getStatusLabel(obra.stageOfWork)}
                          </Badge>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          {saldoRestante.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                          <Box display="flex" alignItems="center" gap={1} mt={1}>
                            <Box flex={1}>
                              <ProgressBar value={percentualRecebido} />
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                              {percentualRecebido.toFixed(1)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }} align="left">
                          {obra.movements.filter(
                            (m) => m.status != ConstructionMovementEnum.CANCELED
                          ).length || 0}
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <div className="flex items-center gap-1">
                            <IconButton
                              title="Editar obra"
                              onClick={() => navigate(`/obra/detalhe/${obra.id}`)}
                            >
                              <Pencil className="h-4 w-4" />
                            </IconButton>
                            <IconButton
                              title="Excluir obra"
                              onClick={() => handleDelete(obra.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <RetornoDaListagemVazia />
                    </TableCell>
                  </TableRow>
                )
              }
            />
            <PaginacaoListagem
              totalCount={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </>
        )}
      </Box>
    </BaseLayout>
  );
}
